import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, tap } from 'rxjs';

import { syncWithLocalStorage } from '@core/helpers';

import { SubscriptionParams } from './web-pushes.entity';
import { WebPushesService } from './web-pushes.service';

@Injectable({
  providedIn: 'root'
})
export class WebPushesState {
  private readonly destroyRef = inject(DestroyRef);
  private readonly webPushesService = inject(WebPushesService);

  public readonly subId = signal<string|null>(null);
  public readonly subscribedAsUser = signal<boolean>(false);
  public readonly nextPromptAt = signal<number|null>(null);
  public readonly promptsCount = signal<number>(0);

  public readonly showPrompt = computed(() => {
    const nextPromptAt = this.nextPromptAt();

    return !this.subId() && (!nextPromptAt || Date.now() >= nextPromptAt);
  });

  private readonly localStorageEffect = syncWithLocalStorage('webpushes', {
    subId: this.subId,
    subscribedAsUser: this.subscribedAsUser,
    nextPromptAt: this.nextPromptAt,
    promptsCount: this.promptsCount
  });

  subscribe(params: SubscriptionParams) {
    return this.webPushesService.createSubscription(params).pipe(
      tap((response) => {
        this.subId.set(response.subscriptionId);
        this.subscribedAsUser.set(response.isLogged);
      })
    );
  }

  unsubscribe(): Observable<null|void> {
    const subId = this.subId();

    if(!subId) {
      return of(null);
    }

    return this.webPushesService.unsubscribe(subId).pipe(
      tap(() => {
        this.subId.set(null);
        this.subscribedAsUser.set(false);
        this.nextPromptAt.set(null);
        this.promptsCount.set(0);
      })
    );
  }

  trackView(tag: number): void {
    const subId = this.subId();

    if(!subId) {
      return;
    }

    this.webPushesService.trackView(tag, subId)
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe();
  }

  trackClick(tag: number, action: string): void {
    const subId = this.subId();

    if(!subId) {
      return;
    }

    this.webPushesService.trackClick(tag, subId, action)
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe();
  }

  linkSubscriptionWithUser(subId: string): void {
    this.webPushesService.linkSubscriptionToUser(subId)
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe(() => {
        this.subId.set(subId);
        this.subscribedAsUser.set(true);
      });
  }

  saveSubscriptionError(error: string): void {
    this.webPushesService.saveSubscriptionError(error)
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe();
  }
  
  declinePrompt(): void {
    const promptsCount = this.promptsCount();

    let nextPopupDays = 0;

    switch(promptsCount) {
      case 0:
        nextPopupDays = 3;
        break;

      case 1:
        nextPopupDays = 7;
        break;

      default:
        nextPopupDays = 30;
        break;
    }

    const nextPromptAt = Date.now() + nextPopupDays * 24 * 3600 * 1000; // hours * seconds * milliseconds

    this.promptsCount.update(value => value + 1);
    this.nextPromptAt.set(nextPromptAt);
  }
}